import { useEffect, useRef } from 'react';
import { Btn, Card, ExternalLink, Icon, Section } from '@ui/library';

export interface SocialEmbedCopy {
  eyebrow: string;
  title: string;
  lead: string;
  xLabel: string;
  xFollowLabel: string;
  /** Fallback text shown if Twitter widgets fail to load. {{handle}} is replaced with the X handle. */
  xFallbackBody: string;
  xOpenLabel: string;
  linkedinLabel: string;
  linkedinSub: string;
  linkedinConnectLabel: string;
  linkedinSeeMoreLabel: string;
  linkedinHighlights: ReadonlyArray<readonly [string, string]>;
}

export interface SocialEmbedProps {
  xHandle: string;
  xHref: string;
  linkedinHref: string;
  copy: SocialEmbedCopy;
}

declare global {
  interface Window {
    twttr?: {
      widgets: {
        load: (target?: HTMLElement | null) => void;
      };
    };
  }
}

function useTwitterWidgets(ref: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://platform.twitter.com/widgets.js"]'
    );
    if (!existing) {
      const s = document.createElement('script');
      s.src = 'https://platform.twitter.com/widgets.js';
      s.async = true;
      document.body.appendChild(s);
    }
    const t = window.setTimeout(() => {
      window.twttr?.widgets.load(ref.current);
    }, 800);
    return () => window.clearTimeout(t);
  }, [ref]);
}

interface SocialHeadProps {
  icon: 'twitter' | 'linkedin';
  iconClass: 'x' | 'li';
  title: string;
  subtitle: string;
  href: string;
  actionLabel: string;
}

function SocialHead({ icon, iconClass, title, subtitle, href, actionLabel }: SocialHeadProps) {
  return (
    <div className="social__head">
      <span className={`social__ic ${iconClass}`}><Icon name={icon} size={18} /></span>
      <div>
        <b>{title}</b>
        <span>{subtitle}</span>
      </div>
      <ExternalLink href={href} className="social__follow">{actionLabel}</ExternalLink>
    </div>
  );
}

export function SocialEmbed({ xHandle, xHref, linkedinHref, copy }: SocialEmbedProps) {
  const xRef = useRef<HTMLDivElement>(null);
  useTwitterWidgets(xRef);

  const fallbackBody = copy.xFallbackBody.replace('{{handle}}', `@${xHandle}`);

  return (
    <Section
      id="social"
      eyebrow={copy.eyebrow}
      title={copy.title}
      lead={copy.lead}
    >
      <div className="social__grid" style={{ marginTop: 40 }}>
        <Card variant="social__card" glow reveal={false}>
          <SocialHead
            icon="twitter"
            iconClass="x"
            title={copy.xLabel}
            subtitle={`@${xHandle}`}
            href={xHref}
            actionLabel={copy.xFollowLabel}
          />
          <div className="social__feed" ref={xRef}>
            <a
              className="twitter-timeline"
              data-theme="dark"
              data-chrome="noheader nofooter noborders transparent"
              data-height="360"
              data-tweet-limit="3"
              href={`https://twitter.com/${xHandle}?ref_src=twsrc%5Etfw`}
            >Posts by @{xHandle}</a>
            <div className="social__fallback">
              <p>{fallbackBody}</p>
              <Btn kind="ghost" href={xHref} target="_blank" icon="arrow-up-right">
                {copy.xOpenLabel}
              </Btn>
            </div>
          </div>
        </Card>

        <Card variant="social__card" glow reveal={false}>
          <SocialHead
            icon="linkedin"
            iconClass="li"
            title={copy.linkedinLabel}
            subtitle={copy.linkedinSub}
            href={linkedinHref}
            actionLabel={copy.linkedinConnectLabel}
          />
          <div className="social__feed li__feed">
            {copy.linkedinHighlights.map(([t, d]) => (
              <ExternalLink key={t} href={linkedinHref} className="li__post">
                <span className="li__dot" />
                <span>
                  <b>{t}</b>
                  <span className="li__txt">{d}</span>
                </span>
              </ExternalLink>
            ))}
            <ExternalLink
              href={linkedinHref}
              className="social__more"
              withArrow
              arrowSize={14}
            >
              {copy.linkedinSeeMoreLabel}
            </ExternalLink>
          </div>
        </Card>
      </div>
    </Section>
  );
}
